"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Ticket } from "lucide-react";
import { useTranslations } from "next-intl";

import { AppAssets } from "@/lib/assets";
import type { TicketingEvent } from "@/lib/ticketing";

const TEAM_CRESTS: Record<string, keyof typeof AppAssets> = {
  "persija":          "persijaCrest",
  "persija jakarta":  "persijaCrest",
  "persib":           "persibCrest",
  "persib bandung":   "persibCrest",
  "persebaya":        "persebayaCrest",
  "persebaya surabaya": "persebayaCrest",
};

function getAwayCrest(awayTeam: string): string {
  const key = TEAM_CRESTS[awayTeam.toLowerCase()];
  return key ? AppAssets[key] : AppAssets.clubCrestCircular;
}

export function MatchCountdown({ event }: { event: TicketingEvent | null }) {
  const t = useTranslations("MatchCountdown");
  const target = event ? new Date(event.kickoff_at).getTime() : 0;
  const [left, setLeft] = useState(() => target - Date.now());

  useEffect(() => {
    if (!target) return;
    const timer = setInterval(() => setLeft(target - Date.now()), 1000);
    return () => clearInterval(timer);
  }, [target]);

  if (!event) return null;

  const total = Math.max(0, Math.floor(left / 1000));
  const parts = [
    { label: t("hari"),   value: Math.floor(total / 86400) },
    { label: t("jam"),    value: Math.floor((total % 86400) / 3600) },
    { label: t("menit"),  value: Math.floor((total % 3600) / 60) },
    { label: t("detik"),  value: total % 60 },
  ];

  return (
    <section className="orange-bloom relative overflow-hidden rounded-2xl border border-outline-variant/20 bg-surface-elevated p-5">
      {/* Ornamen dekoratif */}
      <div aria-hidden className="pointer-events-none absolute -right-8 -top-8 size-36 rounded-full bg-primary/5" />
      <div aria-hidden className="pointer-events-none absolute -bottom-10 -left-6 size-28 rounded-full bg-primary-container/8" />
      <div aria-hidden className="pointer-events-none absolute bottom-10 right-14 size-8 rounded-full bg-primary/4" />
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-2 h-px w-16 -translate-x-1/2 bg-outline-variant/15" />

      <div className="relative flex items-center justify-between">
        <span className="flex items-center gap-2 font-label text-label-caps uppercase text-primary">
          <span className="size-1.5 animate-pulse rounded-full bg-primary-container" />
          {t("nextMatch")}
        </span>
        <span className="font-label text-label-caps text-on-surface-variant">
          {event.competition}
        </span>
      </div>

      <div className="relative mt-stack-md flex items-center justify-between gap-2">
        <TeamBadge name={event.home_team} src={AppAssets.clubCrestCircular} />
        <div className="flex flex-col items-center gap-0.5">
          <p className="font-headline text-body-sm text-on-surface-variant/40">VS</p>
        </div>
        <TeamBadge name={event.away_team} src={getAwayCrest(event.away_team)} />
      </div>

      <div className="relative mt-stack-lg grid grid-cols-4 gap-2">
        {parts.map((part) => (
          <div
            key={part.label}
            className="rounded-xl border border-outline-variant/10 bg-surface-container-lowest py-2.5 text-center"
          >
            <p className="font-headline text-headline-md tabular-nums text-primary" suppressHydrationWarning>
              {String(part.value).padStart(2, "0")}
            </p>
            <p className="font-label text-[10px] uppercase text-on-surface-variant">
              {part.label}
            </p>
          </div>
        ))}
      </div>

      <Link
        href={`/tiket/${event.id}`}
        className="kinetic-gradient relative mt-stack-md flex items-center justify-center gap-2 rounded-full py-3 font-bold text-white transition-transform active:scale-95"
      >
        <Ticket className="size-4" aria-hidden />
        {t("beliTiket")}
      </Link>
    </section>
  );
}

function TeamBadge({ name, src }: { name: string; src: string }) {
  return (
    <div className="flex flex-1 flex-col items-center gap-1.5">
      <div className="relative grid size-14 place-items-center rounded-full bg-surface-container-high p-2">
        <Image src={src} alt="" width={40} height={40} className="size-10 object-contain" />
      </div>
      <span className="text-center font-label text-label-caps uppercase text-on-surface">
        {name}
      </span>
    </div>
  );
}
